import { createSignal, onMount, Show } from "solid-js";
import { Key, SignIn, ShieldCheck } from "phosphor-solid";
import { Button, LinkButton } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Icon } from "@/components/ui/Icon";
import { Input } from "@/components/ui/Input";
import { StateBlock } from "@/components/ui/StateBlock";
import {
  authStore,
  setAuthSessionForLogin,
  type SessionUser,
} from "@/auth/store";
import { config, isAuthConfigured } from "@/config/env";
import { replaceWith, safeInternalPath } from "@/lib/navigation";
import { motion } from "@/lib/motion";
import { ApiError, api } from "@/lib/api";

/**
 * Router search/navigation helpers are not valid under root + lazy routes here,
 * so read the next path from the real URL, same-origin only.
 */
function redirectTarget(): string {
  const target = safeInternalPath(
    new URLSearchParams(window.location.search).get("redirect"),
  );
  return target === "/" ? "/app" : target;
}

type LoginResponse =
  | { status: "authenticated"; user: SessionUser; csrfToken: string }
  | {
      status: "challenge";
      challenge: "NEW_PASSWORD_REQUIRED";
      challengeId: string;
    }
  | {
      status: "unsupported_challenge";
      challenge: string;
      message: string;
    };

function authEndpointLabel(): string {
  return config.apiBaseUrl ? config.apiBaseUrl : window.location.origin;
}

function formatAuthError(err: unknown): string {
  if (err instanceof ApiError) {
    const status = err.status === 0 ? "network" : `HTTP ${err.status}`;
    const details =
      err.details === undefined
        ? ""
        : ` Details: ${JSON.stringify(err.details).slice(0, 500)}`;
    const correlation = err.correlationId
      ? ` Correlation ID: ${err.correlationId}.`
      : "";
    const url = err.url ? ` Endpoint: ${err.url}.` : "";
    return `${status} ${err.code}: ${err.message}.${correlation}${url}${details}`;
  }
  return err instanceof Error ? err.message : String(err);
}

export default function AuthLogin() {
  const [error, setError] = createSignal<string | null>(null);
  const [debug, setDebug] = createSignal<string | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [newPassword, setNewPassword] = createSignal("");
  const [challengeId, setChallengeId] = createSignal<string | null>(
    window.sessionStorage.getItem("conatus.auth.challengeId"),
  );

  onMount(() => {
    if (authStore.isAuthenticated()) {
      replaceWith(redirectTarget());
    }
  });

  async function onSignIn(event: SubmitEvent) {
    event.preventDefault();
    setError(null);
    setDebug(null);
    if (!isAuthConfigured()) {
      setError(
        "Authentication is not configured for this environment (missing VITE_API_BASE_URL).",
      );
      return;
    }
    setSubmitting(true);
    try {
      const result = await api<LoginResponse>("/auth/login", {
        method: "POST",
        authenticated: false,
        body: { email: email(), password: password() },
      });
      handleLoginResult(result);
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onCompletePassword(event: SubmitEvent) {
    event.preventDefault();
    const id = challengeId();
    if (!id) return;
    setError(null);
    setDebug(null);
    setSubmitting(true);
    try {
      const result = await api<LoginResponse>("/auth/complete-password", {
        method: "POST",
        authenticated: false,
        body: { challengeId: id, newPassword: newPassword() },
      });
      handleLoginResult(result);
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleLoginResult(result: LoginResponse) {
    if (result.status === "authenticated") {
      if (!result.user || !result.csrfToken) {
        setError(
          "Login succeeded but the auth response did not include a complete session payload.",
        );
        setDebug(JSON.stringify(result));
        return;
      }
      window.sessionStorage.removeItem("conatus.auth.challengeId");
      setAuthSessionForLogin({ user: result.user, csrfToken: result.csrfToken });
      replaceWith(redirectTarget());
      return;
    }
    if (result.status === "challenge") {
      if (!result.challengeId) {
        setError("Password challenge response did not include a challenge id.");
        setDebug(JSON.stringify(result));
        return;
      }
      window.sessionStorage.setItem("conatus.auth.challengeId", result.challengeId);
      setChallengeId(result.challengeId);
      return;
    }
    setError(
      `${result.message} Challenge: ${result.challenge}. This is expected if the user requires MFA before v1 MFA support ships.`,
    );
  }

  function resetChallenge() {
    window.sessionStorage.removeItem("conatus.auth.challengeId");
    setChallengeId(null);
    setNewPassword("");
    setError(null);
  }

  return (
    <div class="mx-auto flex min-h-[55vh] max-w-lg items-center">
      <Card class={`w-full overflow-hidden ${motion.page}`}>
        <CardHeader class="border-b border-border bg-card/60">
          <div class="mb-2 flex size-11 items-center justify-center rounded border border-border bg-background text-primary">
            <Icon as={ShieldCheck} size="lg" weight="duotone" />
          </div>
          <CardTitle class="text-2xl">Sign in</CardTitle>
          <CardDescription>
            Use your Conatus account to enter the operations workspace.
          </CardDescription>
        </CardHeader>
        <CardContent class="space-y-4 pt-4">
          <Show
            when={challengeId()}
            fallback={
              <form class="space-y-4" onSubmit={onSignIn}>
                <label class="block space-y-1 text-sm">
                  <span class="font-medium">Email</span>
                  <Input
                    type="email"
                    autocomplete="username"
                    value={email()}
                    onInput={(event) => setEmail(event.currentTarget.value)}
                    required
                  />
                </label>
                <label class="block space-y-1 text-sm">
                  <span class="font-medium">Password</span>
                  <Input
                    type="password"
                    autocomplete="current-password"
                    value={password()}
                    onInput={(event) => setPassword(event.currentTarget.value)}
                    required
                  />
                </label>
                <Button
                  class="w-full justify-center"
                  type="submit"
                  disabled={submitting() || !isAuthConfigured()}
                >
                  <Icon as={SignIn} />
                  <Show when={submitting()} fallback="Sign in">
                    Signing in...
                  </Show>
                </Button>
                <LinkButton
                  href="/auth/forgot-password"
                  variant="ghost"
                  class="w-full justify-center text-xs"
                >
                  Forgot password?
                </LinkButton>
              </form>
            }
          >
            <form class="space-y-4" onSubmit={onCompletePassword}>
              <StateBlock
                variant="info"
                title="Set a new password"
                size="compact"
              >
                Your temporary invite password was accepted. Choose a permanent
                password to finish signing in.
              </StateBlock>
              <label class="block space-y-1 text-sm">
                <span class="font-medium">New password</span>
                <Input
                  type="password"
                  autocomplete="new-password"
                  value={newPassword()}
                  onInput={(event) => setNewPassword(event.currentTarget.value)}
                  minlength="12"
                  required
                />
              </label>
              <div class="grid grid-cols-2 gap-2">
                <Button class="justify-center" type="submit" disabled={submitting()}>
                  <Icon as={Key} />
                  <Show when={submitting()} fallback="Set password">
                    Saving...
                  </Show>
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={resetChallenge}
                  disabled={submitting()}
                >
                  Start over
                </Button>
              </div>
            </form>
          </Show>

          <p class="text-xs text-muted-foreground">
            Access is controlled by tenant role assignments and feature entitlements.
            {" "}Auth endpoint: {authEndpointLabel()}.
          </p>

          <Show when={authStore.error()}>
            {(sessionError) => (
              <StateBlock
                variant="info"
                title="Session status"
                size="compact"
              >
                {sessionError().message}
              </StateBlock>
            )}
          </Show>

          <Show when={error()}>
            {(message) => (
              <StateBlock variant="error" title="Sign-in failed" size="compact">
                {message()}
              </StateBlock>
            )}
          </Show>

          <Show when={debug()}>
            {(value) => (
              <details class="rounded border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
                <summary class="cursor-pointer font-medium text-foreground">
                  Debug payload
                </summary>
                <pre class="mt-2 whitespace-pre-wrap break-words">{value()}</pre>
              </details>
            )}
          </Show>
        </CardContent>
      </Card>
    </div>
  );
}
